import { createTheme } from "@mui/material/styles";
import {
  type ColorMode,
  getColors,
  riskColors,
  chartColors,
} from "./tokens/colors.ts";
import { typography } from "./tokens/typography.ts";
import { getMuiButton } from "./overrides/MuiButton.ts";
import { getMuiCard } from "./overrides/MuiCard.ts";
import { getMuiTextField } from "./overrides/MuiTextField.ts";
import { getMuiChip } from "./overrides/MuiChip.ts";
import { getMuiPaper } from "./overrides/MuiPaper.ts";

declare module "@mui/material/styles" {
  interface Palette {
    risk: { high: string; medium: string; low: string };
    chart: {
      primary: string;
      secondary: string;
      tertiary: string;
      quaternary: string;
      quinary: string;
    };
    surfaceContainer: string;
    surfaceContainerLow: string;
    surfaceContainerHigh: string;
    surfaceContainerHighest: string;
    onSurfaceVariant: string;
    outlineVariant: string;
  }
  interface PaletteOptions {
    risk?: { high: string; medium: string; low: string };
    chart?: {
      primary: string;
      secondary: string;
      tertiary: string;
      quaternary: string;
      quinary: string;
    };
    surfaceContainer?: string;
    surfaceContainerLow?: string;
    surfaceContainerHigh?: string;
    surfaceContainerHighest?: string;
    onSurfaceVariant?: string;
    outlineVariant?: string;
  }
}

export function createAegisTheme(mode: ColorMode) {
  const c = getColors(mode);
  const risk = {
    high: riskColors.high[mode],
    medium: riskColors.medium[mode],
    low: riskColors.low[mode],
  };
  const chart = chartColors[mode];

  return createTheme({
    palette: {
      mode,
      primary: {
        main: c.primary,
        light: mode === "dark" ? c.primaryFixed : c.primaryFixedDim,
        dark: c.primaryContainer,
        contrastText: c.onPrimary,
      },
      secondary: {
        main: c.secondary,
        light: mode === "dark" ? c.secondaryFixed : c.secondaryFixedDim,
        dark: c.secondaryContainer,
        contrastText: c.onSecondary,
      },
      error: {
        main: c.error,
        light: c.onErrorContainer,
        dark: c.errorContainer,
        contrastText: c.onError,
      },
      background: {
        default: c.surface,
        paper: mode === "dark" ? c.surfaceContainer : c.surfaceContainerLowest,
      },
      text: {
        primary: c.onSurface,
        secondary: c.onSurfaceVariant,
      },
      divider: `${c.outlineVariant}26`,
      risk,
      chart,
      surfaceContainer: c.surfaceContainer,
      surfaceContainerLow: c.surfaceContainerLow,
      surfaceContainerHigh: c.surfaceContainerHigh,
      surfaceContainerHighest: c.surfaceContainerHighest,
      onSurfaceVariant: c.onSurfaceVariant,
      outlineVariant: c.outlineVariant,
    },
    typography: {
      fontFamily: typography.fontFamily,
      h1: { ...typography.display },
      h2: { ...typography.display },
      h3: { ...typography.headline },
      h4: { ...typography.headline },
      h5: { ...typography.title },
      h6: { ...typography.title, fontSize: "1rem" },
      body1: { ...typography.body },
      body2: { ...typography.body, fontSize: "0.875rem" },
      subtitle1: { letterSpacing: "-0.01em", fontWeight: 500 },
      subtitle2: { letterSpacing: "-0.01em", fontWeight: 500 },
      overline: { ...typography.label, fontSize: "0.6875rem" },
    },
    shape: {
      borderRadius: 6,
    },
    components: {
      MuiButton: getMuiButton(mode),
      MuiCard: getMuiCard(mode),
      MuiTextField: getMuiTextField(mode),
      MuiChip: getMuiChip(mode),
      MuiPaper: getMuiPaper(mode),
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: "none",
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: c.surface,
          },
        },
      },
    },
  });
}
